import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, Pencil, Trash2, CheckCircle, Clock, User, Plus, MessageSquare } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';
import useAuth from '../../../../hooks/useAuth';

export default function TaskDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newNote, setNewNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [completing, setCompleting] = useState(false);

    const fetchTask = async () => {
        try {
            const res = await api.get(`/tasks/${id}`);
            setTask(res.data);
        } catch {
            toast.error('Failed to load task');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTask(); }, [id]);

    const handleComplete = async () => {
        setCompleting(true);
        try {
            await api.patch(`/tasks/${id}/complete`);
            setTask(p => ({ ...p, is_completed: true }));
            toast.success('Task marked as complete');
        } catch {
            toast.error('Failed to update task');
        } finally {
            setCompleting(null);
        }
    };

    const handleSaveNote = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;
        setSavingNote(true);
        try {
            const res = await api.post(`/tasks/${id}/notes`, { content: newNote });
            setTask(prev => ({
                ...prev,
                notes_history: [res.data, ...(prev.notes_history || [])]
            }));
            setNewNote('');
            toast.success('Note added to history');
        } catch {
            toast.error('Failed to save note');
        } finally {
            setSavingNote(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            await api.delete(`/task-notes/${noteId}`);
            setTask(prev => ({
                ...prev,
                notes_history: prev.notes_history.filter(n => n.id !== noteId)
            }));
            toast.success('Note removed');
        } catch {
            toast.error('Failed to delete note');
        }
    };

    if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;
    if (!task) return <div className="text-center py-20 text-gray-400">Task not found</div>;

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
            <Toaster position="top-right" richColors />
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/admin/tasks')} className="p-2 hover:bg-gray-200 rounded-lg shrink-0"><ArrowLeft className="w-5 h-5" /></button>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">{task.title}</h2>
                            <div className="flex items-center gap-2 mt-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.is_completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                    {task.is_completed ? 'Completed' : 'Pending'}
                                </span>
                                {task.due_date && (
                                    <span className="text-xs text-gray-500 flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> Due: {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {!task.is_completed && (
                            <button onClick={handleComplete} disabled={completing} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                                {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                Complete
                            </button>
                        )}
                        <button onClick={() => navigate(`/admin/tasks/${id}/edit`)} className="p-2 text-gray-600 hover:bg-gray-100 border border-gray-200 rounded-lg">
                            <Pencil className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Task Info */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
                            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Information</h2>
                            
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs text-gray-400 uppercase">Assigned To</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                            <User className="w-4 h-4" />
                                        </div>
                                        <p className="text-sm font-medium text-gray-800">{task.assigned_user?.name || 'Unassigned'}</p>
                                    </div>
                                </div>

                                {task.lead && (
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase">Linked Lead</p>
                                        <button onClick={() => navigate(`/admin/leads/${task.lead.id}`)} className="text-sm font-medium text-blue-600 hover:underline block mt-1">
                                            {task.lead.name}
                                        </button>
                                        <p className="text-xs text-gray-500">{task.lead.phone}</p>
                                    </div>
                                )}

                                <div>
                                    <p className="text-xs text-gray-400 uppercase">Created On</p>
                                    <p className="text-sm text-gray-800 mt-1">{new Date(task.created_at).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>

                        {/* Task Original Notes (Summary) */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Initial Summary</h2>
                            <p className="text-sm text-gray-600 italic whitespace-pre-wrap">{task.notes || 'No initial summary provided.'}</p>
                        </div>
                    </div>

                    {/* Right: Notes History (Timeline) */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-blue-600" />
                                Follow-Up History
                            </h2>

                            {/* Add Note Form */}
                            <form onSubmit={handleSaveNote} className="mb-8">
                                <textarea
                                    value={newNote}
                                    onChange={e => setNewNote(e.target.value)}
                                    placeholder="Add a progress update, call note, or next step..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                                    style={{ color: '#000000', backgroundColor: '#ffffff' }}
                                />
                                <div className="flex justify-end mt-2">
                                    <button
                                        type="submit"
                                        disabled={savingNote || !newNote.trim()}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
                                    >
                                        {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                        Add Update
                                    </button>
                                </div>
                            </form>

                            {/* Timeline */}
                            <div className="relative border-l-2 border-gray-100 ml-3 space-y-8">
                                {task.notes_history?.length === 0 ? (
                                    <div className="pl-8 py-4 text-center text-gray-400 italic">No historical updates yet.</div>
                                ) : (
                                    task.notes_history.map(note => (
                                        <div key={note.id} className="relative pl-8">
                                            {/* dot */}
                                            <div className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-blue-500"></div>
                                            
                                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <span className="font-bold text-gray-900 text-sm">{note.user?.name}</span>
                                                        <span className="text-xs text-gray-500 ml-2">{new Date(note.created_at).toLocaleString()}</span>
                                                    </div>
                                                    {(isAdmin || note.user_id === task.assigned_to) && (
                                                        <button onClick={() => handleDeleteNote(note.id)} className="text-gray-400 hover:text-red-500 transition">
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
